import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, Calendar, Eye } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { authService } from '@/utils/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminContacts = () => {
  const { t, i18n } = useTranslation();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/admin/contacts`, {
        headers: authService.getAuthHeader()
      });
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleView = (contact) => {
    setSelectedContact(contact);
    setViewDialogOpen(true);
  };

  return (
    <div data-testid="admin-contacts-page">
      <h1 className="font-heading text-4xl mb-8 tracking-tight">{t('admin.contacts')}</h1>

      {loading ? (
        <div className="text-center py-12">Загрузка...</div>
      ) : (
        <div className="bg-background rounded-sm border border-border overflow-hidden">
          <table className="w-full" data-testid="contacts-table">
            <thead className="bg-muted">
              <tr>
                <th className="px-6 py-4 text-left font-body text-sm uppercase tracking-wide">{t('contact.name')}</th>
                <th className="px-6 py-4 text-left font-body text-sm uppercase tracking-wide">{t('contact.phone')}</th>
                <th className="px-6 py-4 text-left font-body text-sm uppercase tracking-wide">{t('contact.email')}</th>
                <th className="px-6 py-4 text-left font-body text-sm uppercase tracking-wide">{t('admin.product')}</th>
                <th className="px-6 py-4 text-left font-body text-sm uppercase tracking-wide">{t('admin.date')}</th>
                <th className="px-6 py-4 text-left font-body text-sm uppercase tracking-wide">{t('admin.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((contact, index) => (
                <tr key={contact.id} className="border-t border-border hover:bg-muted/50" data-testid={`contact-row-${index}`}>
                  <td className="px-6 py-4 font-medium">{contact.name}</td>
                  <td className="px-6 py-4">
                    <a href={`tel:${contact.phone}`} className="flex items-center text-accent hover:underline">
                      <Phone className="w-4 h-4 mr-2" />
                      {contact.phone}
                    </a>
                  </td>
                  <td className="px-6 py-4">
                    {contact.email ? (
                      <a href={`mailto:${contact.email}`} className="flex items-center text-accent hover:underline">
                        <Mail className="w-4 h-4 mr-2" />
                        {contact.email}
                      </a>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {contact.product_id ? (
                      <span className="text-sm">{contact.product_id}</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center text-muted-foreground">
                      <Calendar className="w-4 h-4 mr-2" />
                      {new Date(contact.created_at).toLocaleString(i18n.language === 'ru' ? 'ru-RU' : 'lv-LV')}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleView(contact)}
                      data-testid={`view-button-${index}`}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      {t('admin.view')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="view-contact-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight">Детали заявки</DialogTitle>
          </DialogHeader>

          {selectedContact && (
            <div className="space-y-4 py-4">
              <div>
                <p className="font-body text-sm text-muted-foreground uppercase tracking-wide mb-1">
                  {t('contact.name')}
                </p>
                <p className="font-medium">{selectedContact.name}</p>
              </div>

              <div>
                <p className="font-body text-sm text-muted-foreground uppercase tracking-wide mb-1">
                  {t('contact.phone')}
                </p>
                <a href={`tel:${selectedContact.phone}`} className="text-accent hover:underline">
                  {selectedContact.phone}
                </a>
              </div>

              {selectedContact.email && (
                <div>
                  <p className="font-body text-sm text-muted-foreground uppercase tracking-wide mb-1">
                    {t('contact.email')}
                  </p>
                  <a href={`mailto:${selectedContact.email}`} className="text-accent hover:underline">
                    {selectedContact.email}
                  </a>
                </div>
              )}

              <div>
                <p className="font-body text-sm text-muted-foreground uppercase tracking-wide mb-1">
                  {t('contact.message')}
                </p>
                <p className="whitespace-pre-wrap">{selectedContact.message}</p>
              </div>

              {selectedContact.product_id && (
                <div>
                  <p className="font-body text-sm text-muted-foreground uppercase tracking-wide mb-1">
                    {t('admin.product')}
                  </p>
                  <p>{selectedContact.product_id}</p>
                </div>
              )}

              <div>
                <p className="font-body text-sm text-muted-foreground uppercase tracking-wide mb-1">
                  {t('admin.date')}
                </p>
                <p>{new Date(selectedContact.created_at).toLocaleString(i18n.language === 'ru' ? 'ru-RU' : 'lv-LV')}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
